// ── FIX #9 is inside SalesLayout → bottom tab bar → active indicator ─────────
// The original had: position:'absolute', top:0 inside a flex button with no
// relative parent, so the bar floated off-screen.
// Fix: add position:'relative' to the button and correct the indicator styles.
//
// All other Layout code is unchanged from original.
// Search for "FIX #9" below to find the exact change.

import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import AdminNotificationBar from './AdminNotificationBar'
import ProfilePhoto from './ProfilePhoto'
import { getEffectiveTheme, toggleTheme } from '../theme'

// ── Light/dark toggle — dropped into every nav layout below. Defaults to
// whatever the OS prefers (see src/theme.js); clicking it saves an explicit
// per-browser override.
function ThemeToggle({ compact }) {
  const [theme, setTheme] = useState(getEffectiveTheme)
  const isLight = theme === 'light'
  return (
    <button
      onClick={() => setTheme(toggleTheme())}
      title={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
      aria-label="Toggle light/dark theme"
      style={{
        width: compact ? '28px' : '32px', height: compact ? '28px' : '32px',
        borderRadius: '8px', border: '0.5px solid rgba(var(--fg-rgb), 0.12)',
        background: 'rgba(var(--fg-rgb), 0.04)', color: 'rgba(var(--fg-rgb), 0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit',
      }}>
      {isLight ? (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="8" cy="8" r="3.5"/><path d="M8 1v1.5M8 13.5V15M15 8h-1.5M2.5 8H1M12.9 3.1l-1 1M4.1 11.9l-1 1M12.9 12.9l-1-1M4.1 4.1l-1-1"/></svg>
      ) : (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 8.7A6 6 0 117.3 2 4.7 4.7 0 0014 8.7z"/></svg>
      )}
    </button>
  )
}

const ROLES = {
  admin:   { label:'Admin',   color:'#e87c1e', bg:'rgba(232,124,30,0.15)', border:'rgba(232,124,30,0.3)', accent:'#e87c1e' },
  teacher: { label:'Teacher', color:'#1e90ff', bg:'rgba(30,144,255,0.15)', border:'rgba(30,144,255,0.3)', accent:'#1e90ff' },
  student: { label:'Student', color:'#10b981', bg:'rgba(16,185,129,0.15)', border:'rgba(16,185,129,0.3)', accent:'#10b981' },
  sales:   { label:'Sales',   color:'#8b5cf6', bg:'rgba(139,92,246,0.15)', border:'rgba(139,92,246,0.3)', accent:'#8b5cf6' },
}

const ROLE_PORTAL = {
  admin: 'Admin Portal', teacher: 'Teacher Portal',
  sales: 'Sales Portal', student: 'Student Portal',
}

const NAV_ITEMS = {
  admin: [
    { id:'overview',      icon:'chart',   label:'Overview',       group:'OVERVIEW'  },
    { id:'support-inbox', icon:'chat',    label:'Support Inbox',  group:'OVERVIEW'  },
    { id:'leads',         icon:'leads',   label:'All Leads',      group:'LEADS'     },
    { id:'schedule-demo', icon:'play',    label:'Schedule Demo',  group:'LEADS'     },
    { id:'adleads',       icon:'funnel',  label:'From Ads',       group:'LEADS'     },
    { id:'sales-perms',   icon:'shield',  label:'Sales Perms',    group:'LEADS'     },
    { id:'users',         icon:'users',   label:'Users & Roles',  group:'STUDENTS'  },
    { id:'directory',     icon:'book',    label:'Directory',      group:'STUDENTS'  },
    { id:'enrol',         icon:'check',   label:'Enrol Student',  group:'STUDENTS'  },
    { id:'payments',      icon:'card',    label:'Payments',       group:'STUDENTS'  },
    { id:'feereminder',   icon:'invoice', label:'Fee Reminders',  group:'STUDENTS'  },
    { id:'classes',       icon:'cal',     label:'All Classes',    group:'CLASSES'   },
    { id:'schedule',      icon:'video',   label:'Schedule Class', group:'CLASSES'   },
    { id:'availability',  icon:'cal2',    label:'Teacher Availability', group:'CLASSES' },
    { id:'reschedule',    icon:'cal2',    label:'Reschedule',     group:'CLASSES'   },
    { id:'reminders',     icon:'chat',    label:'Reminders',      group:'CLASSES'   },
    { id:'batchreset',    icon:'users',   label:'Manage Student', group:'CLASSES'   },
    { id:'bulkcancel',    icon:'upload',  label:'Bulk Cancel',    group:'CLASSES'   },
    { id:'bulkreschedule',icon:'cal2',    label:'Bulk Reschedule',group:'CLASSES'   },
    { id:'clockchange',   icon:'cal2',    label:'Clock Change',   group:'CLASSES'   },
    { id:'reimbursements',icon:'cal2',    label:'Reimburse Class',group:'CLASSES'   },
    { id:'enrollexisting',icon:'check',   label:'Enroll in Classes',group:'CLASSES' },
    { id:'courses',       icon:'courses', label:'Courses',        group:'CONTENT'   },
    { id:'resources',     icon:'book',    label:'Resources',      group:'CONTENT'   },
    { id:'recordings',    icon:'play',    label:'Recordings',     group:'CONTENT'   },
    { id:'calendar',      icon:'cal',     label:'Calendar',       group:'ANALYTICS' },
    { id:'activitylog',   icon:'chart',   label:'Activity Log',   group:'ANALYTICS' },
    { id:'health',        icon:'check',   label:'System Health',  group:'ANALYTICS' },
  ],
  teacher: [
    { id:'classes',    icon:'video', label:'Classes'     },
    { id:'students',   icon:'users', label:'My Students' },
    { id:'attendance', icon:'check', label:'Attendance'  },
    { id:'availability', icon:'cal2', label:'My Availability' },
    { id:'calendar',   icon:'cal',   label:'Calendar'    },
    { id:'resources',  icon:'book',  label:'Resources'   },
    { id:'recordings', icon:'play',  label:'Recordings'  },
    { id:'reschedule', icon:'cal2',  label:'Reschedule'  },
    { id:'support',    icon:'chat',  label:'Support'     },
  ],
  sales: [
    { id:'leads',      icon:'leads', label:'Leads'         },
    { id:'demo',       icon:'play',  label:'Schedule Demo' },
  ],
}

function NavIcon({ type, size = 16 }) {
  const s = { width: size, height: size, flexShrink: 0 }
  const icons = {
    chart:   <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="1" y="8" width="3" height="6" rx="0.5"/><rect x="6" y="5" width="3" height="9" rx="0.5"/><rect x="11" y="2" width="3" height="12" rx="0.5"/></svg>,
    users:   <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="6" cy="5" r="2.5"/><path d="M1 13c0-2.76 2.24-5 5-5s5 2.24 5 5"/><circle cx="12" cy="5" r="2"/><path d="M15 13c0-2-1.34-3.7-3.2-4.3"/></svg>,
    card:    <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="1" y="3" width="14" height="10" rx="1.5"/><path d="M1 7h14"/><path d="M4 10.5h2"/></svg>,
    courses: <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M8 2L14 5v4c0 3-2.5 4.5-6 5C2.5 13.5 0 12 0 9V5l6-3z" transform="translate(1,0)"/><path d="M8 2v12M5 6l3 2 3-2"/></svg>,
    cal:     <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="1" y="2" width="14" height="13" rx="1.5"/><path d="M1 7h14M5 1v3M11 1v3"/></svg>,
    cal2:    <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="1" y="2" width="14" height="13" rx="1.5"/><path d="M1 7h14M5 1v3M11 1v3M5 10h2M9 10h2"/></svg>,
    book:    <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 2h9a1 1 0 011 1v10a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z"/><path d="M8 2v12M5 5h1.5M5 7.5h1.5M5 10h1.5"/></svg>,
    video:   <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="1" y="3" width="9" height="10" rx="1.5"/><path d="M10 6l4-2v8l-4-2"/></svg>,
    check:   <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="8" cy="8" r="6.5"/><path d="M5 8l2 2 4-4"/></svg>,
    chat:    <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M14 9.5a6 6 0 01-8.4 5.5L2 16l1-3.6A6 6 0 118 2h.5A6 6 0 0114 8v1.5z"/></svg>,
    leads:   <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="8" cy="5" r="3"/><path d="M2 14c0-3.31 2.69-6 6-6s6 2.69 6 6"/><path d="M10.5 3.5l2-2M12.5 1.5l1.5 1.5"/></svg>,
    upload:  <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M8 10V2M5 5l3-3 3 3"/><path d="M2 12v1a1 1 0 001 1h10a1 1 0 001-1v-1"/></svg>,
    funnel:  <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M2 2h12l-4.5 6v5l-3-1.5V8z"/></svg>,
    play:    <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="8" cy="8" r="6.5"/><path d="M6.5 5.5l5 2.5-5 2.5z"/></svg>,
    invoice: <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="2" y="1" width="12" height="14" rx="1.5"/><path d="M5 5h6M5 8h6M5 11h4"/></svg>,
    shield:  <svg {...s} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M8 1L2 4v4c0 3.5 2.5 6 6 7 3.5-1 6-3.5 6-7V4z"/><path d="M5.5 8l2 2 3-3"/></svg>,
  }
  return icons[type] || icons.book
}

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 768)
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [])
  return m
}

function SidebarLayout({ profile, navItems, activeTab, onNavClick, portalLabel, children }) {
  const cfg = ROLES[profile.role] || ROLES.student
  const [sidebarOpen, setSidebarOpen] = useState(false)

  function handleNav(id) {
    onNavClick(id)
    setSidebarOpen(false) // auto-close the drawer on mobile after picking a page
  }

  return (
    <div className="lms-shell">
      <button className="lms-hamburger" onClick={() => setSidebarOpen(o => !o)} aria-label="Toggle menu">☰</button>
      <div className={`lms-sidebar-overlay${sidebarOpen ? ' open' : ''}`} onClick={() => setSidebarOpen(false)} />

      <div className={`lms-sidebar${sidebarOpen ? ' open' : ''}`} style={{ background:'var(--bg-page)', borderRight:'0.5px solid rgba(var(--fg-rgb),0.07)' }}>
        <div style={{ padding:'22px 18px 16px', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.06)' }}>
          <div style={{ fontFamily:"'Arial Black',sans-serif", fontWeight:900, letterSpacing:'-0.02em' }}>
            <span style={{ color:'#1e90ff', fontSize:'22px' }}>UNI</span>
            <span style={{ color:'#e87c1e', fontSize:'22px' }}>EDD</span>
          </div>
          <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a22))', marginTop:'4px', letterSpacing:'0.07em', textTransform:'uppercase' }}>{portalLabel}</div>
        </div>

        <div style={{ padding:'14px 16px', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.06)', display:'flex', alignItems:'center', gap:'10px' }}>
          <ProfilePhoto profile={profile} size={34} />
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a85))', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:1.2 }}>
              {profile.full_name || profile.email}
            </div>
            <span style={{ display:'inline-block', marginTop:'4px', fontSize:'9px', padding:'2px 8px', borderRadius:'10px', fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', background:cfg.bg, color:cfg.color, border:`0.5px solid ${cfg.border}` }}>
              {cfg.label}
            </span>
          </div>
        </div>

        <nav style={{ padding:'8px', flex:1, overflowY:'auto' }}>
          {navItems[0]?.group ? (() => {
            const groups = []
            const seen = {}
            navItems.forEach(item => {
              if (!seen[item.group]) { seen[item.group] = true; groups.push({ name: item.group, items:[] }) }
              groups[groups.length-1].items.push(item)
            })
            return groups.map(g => (
              <div key={g.name}>
                <div style={{ fontSize:'9px', fontWeight:800, color:'rgba(var(--fg-rgb),0.18)', letterSpacing:'0.12em', padding:'10px 8px 4px' }}>{g.name}</div>
                {g.items.map(item => {
                  const isActive = activeTab === item.id
                  return (
                    <button key={item.id} onClick={() => handleNav(item.id)} style={{ display:'flex', alignItems:'center', gap:'9px', width:'100%', padding:'8px 10px', borderRadius:'8px', border:'none', cursor:'pointer', fontFamily:'inherit', textAlign:'left', marginBottom:'1px', transition:'all 0.1s', background: isActive ? cfg.bg : 'transparent', color: isActive ? cfg.color : 'rgba(var(--fg-rgb),var(--a38))', fontWeight: isActive ? 600 : 400, fontSize:'12.5px', borderLeft: isActive ? `3px solid ${cfg.color}` : '3px solid transparent' }}>
                      <NavIcon type={item.icon} size={14} />
                      <span style={{ flex:1 }}>{item.label}</span>
                    </button>
                  )
                })}
              </div>
            ))
          })() : navItems.map(item => {
            const isActive = activeTab === item.id
            return (
              <button key={item.id} onClick={() => handleNav(item.id)} style={{ display:'flex', alignItems:'center', gap:'11px', width:'100%', padding:'10px 10px', borderRadius:'9px', border:'none', cursor:'pointer', fontFamily:'inherit', textAlign:'left', marginBottom:'2px', transition:'all 0.12s', background: isActive ? cfg.bg : 'transparent', color: isActive ? cfg.color : 'rgba(var(--fg-rgb),var(--a38))', fontWeight: isActive ? 600 : 400, fontSize:'13px' }}>
                <NavIcon type={item.icon} size={15} />
                <span style={{ flex:1 }}>{item.label}</span>
                {isActive && <div style={{ width:'3px', height:'18px', background:cfg.color, borderRadius:'2px', flexShrink:0 }} />}
              </button>
            )
          })}
        </nav>

        <div style={{ padding:'12px', borderTop:'0.5px solid rgba(var(--fg-rgb),0.06)', display:'flex', gap:'8px', alignItems:'center' }}>
          <button onClick={() => supabase.auth.signOut()} style={{ flex:1, padding:'9px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a30))', background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'8px', cursor:'pointer', fontFamily:'inherit' }}>Sign out</button>
          <ThemeToggle />
        </div>
      </div>

      <main className="lms-main" style={{ maxWidth:'1200px' }}>
        {profile.role === 'admin' && <AdminNotificationBar profile={profile} />}
        {children}
      </main>
    </div>
  )
}

function SalesLayout({ profile, activeTab, onNavClick, navItems: navOverride, children }) {
  const cfg = ROLES.sales
  const isMobile = useIsMobile()
  const navItems = navOverride || NAV_ITEMS.sales

  if (isMobile) {
    return (
      <div style={{ minHeight:'100vh', background:'var(--bg-shell)', display:'flex', flexDirection:'column' }}>
        <div style={{ background:'var(--bg-page)', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.07)', padding:'0 16px', height:'52px', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100, flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <div style={{ fontFamily:"'Arial Black',sans-serif", fontWeight:900 }}>
              <span style={{ color:'#1e90ff', fontSize:'18px' }}>UNI</span>
              <span style={{ color:'#e87c1e', fontSize:'18px' }}>EDD</span>
            </div>
            <span style={{ fontSize:'9px', padding:'2px 8px', borderRadius:'10px', fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', background:cfg.bg, color:cfg.color, border:`0.5px solid ${cfg.border}` }}>
              Sales
            </span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a50))', maxWidth:'100px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {profile.full_name?.split(' ')[0]}
            </span>
            <ProfilePhoto profile={profile} size={30} />
            <ThemeToggle compact />
            <button onClick={() => supabase.auth.signOut()} style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', background:'none', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'6px', padding:'4px 8px', cursor:'pointer', fontFamily:'inherit' }}>
              Out
            </button>
          </div>
        </div>

        <main style={{ flex:1, overflowY:'auto', padding:'14px 12px 80px', WebkitOverflowScrolling:'touch' }}>
          {children}
        </main>

        <div style={{ position:'fixed', bottom:0, left:0, right:0, background:'var(--bg-page)', borderTop:'0.5px solid rgba(var(--fg-rgb),0.1)', display:'flex', zIndex:100, paddingBottom:'env(safe-area-inset-bottom)' }}>
          {navItems.map(item => {
            const isActive = activeTab === item.id
            return (
              // FIX #9: added position:'relative' so the active indicator anchors correctly
              <button key={item.id} onClick={() => onNavClick(item.id)} style={{
                flex:1, padding:'10px 4px 8px',
                border:'none', background:'transparent',
                display:'flex', flexDirection:'column', alignItems:'center', gap:'4px',
                cursor:'pointer',
                color: isActive ? cfg.color : 'rgba(var(--fg-rgb),var(--a30))',
                transition:'all .15s',
                position:'relative',  // ← FIX #9
              }}>
                {/* FIX #9: indicator now sits at top of the button, not floating off */}
                {isActive && (
                  <div style={{
                    position:'absolute',
                    top:0, left:'50%', transform:'translateX(-50%)',
                    width:'30px', height:'2px',
                    background:cfg.color,
                    borderRadius:'0 0 2px 2px',
                  }} />
                )}
                <NavIcon type={item.icon} size={20} />
                <span style={{ fontSize:'10px', fontWeight: isActive ? 700 : 400 }}>{item.label}</span>
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  // Desktop — small sidebar for sales
  return (
    <div style={{ minHeight:'100vh', background:'var(--bg-shell)', display:'flex' }}>
      <div style={{ width:'180px', flexShrink:0, background:'var(--bg-page)', borderRight:'0.5px solid rgba(var(--fg-rgb),0.07)', display:'flex', flexDirection:'column', position:'sticky', top:0, height:'100vh', overflowY:'auto' }}>
        <div style={{ padding:'20px 16px 14px', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.06)' }}>
          <div style={{ fontFamily:"'Arial Black',sans-serif", fontWeight:900 }}>
            <span style={{ color:'#1e90ff', fontSize:'20px' }}>UNI</span>
            <span style={{ color:'#e87c1e', fontSize:'20px' }}>EDD</span>
          </div>
          <div style={{ fontSize:'9px', color:'rgba(var(--fg-rgb),var(--a20))', marginTop:'3px', letterSpacing:'0.07em', textTransform:'uppercase' }}>Sales Portal</div>
        </div>

        <div style={{ padding:'12px 14px', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.06)', display:'flex', alignItems:'center', gap:'8px' }}>
          <ProfilePhoto profile={profile} size={30} />
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:'12px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a80))', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {profile.full_name?.split(' ')[0] || 'Sales'}
            </div>
            <span style={{ fontSize:'9px', padding:'1px 6px', borderRadius:'8px', fontWeight:700, background:cfg.bg, color:cfg.color }}>Sales</span>
          </div>
        </div>

        <nav style={{ padding:'8px', flex:1 }}>
          {navItems.map(item => {
            const isActive = activeTab === item.id
            return (
              <button key={item.id} onClick={() => onNavClick(item.id)} style={{ display:'flex', alignItems:'center', gap:'10px', width:'100%', padding:'10px 10px', borderRadius:'9px', border:'none', cursor:'pointer', fontFamily:'inherit', textAlign:'left', marginBottom:'2px', transition:'all 0.12s', background: isActive ? cfg.bg : 'transparent', color: isActive ? cfg.color : 'rgba(var(--fg-rgb),var(--a40))', fontWeight: isActive ? 600 : 400, fontSize:'13px' }}>
                <NavIcon type={item.icon} size={15} />
                <span style={{ flex:1 }}>{item.label}</span>
              </button>
            )
          })}
        </nav>

        <div style={{ padding:'10px', borderTop:'0.5px solid rgba(var(--fg-rgb),0.06)', display:'flex', gap:'8px', alignItems:'center' }}>
          <button onClick={() => supabase.auth.signOut()} style={{ flex:1, padding:'8px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a30))', background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'8px', cursor:'pointer', fontFamily:'inherit' }}>Sign out</button>
          <ThemeToggle />
        </div>
      </div>

      <main style={{ flex:1, padding:'2rem', minWidth:0 }}>
        {children}
      </main>
    </div>
  )
}

// navItems: optional per-user menu (the sales dashboard passes the sections
// admin has enabled for that salesperson).
export default function Layout({ profile, pageTitle, children, activeTab, onTabChange, navItems: navOverride }) {
  const role = profile.role
  const [internalTab, setInternalTab] = useState(
    role === 'admin' ? 'overview' :
    role === 'teacher' ? 'classes' :
    role === 'sales' ? 'leads' : null
  )

  const navItems    = NAV_ITEMS[role]
  const portalLabel = ROLE_PORTAL[role] || 'Portal'
  const currentTab  = activeTab ?? internalTab
  const handleClick = onTabChange ?? setInternalTab

  if (role === 'sales') {
    return (
      <SalesLayout profile={profile} activeTab={currentTab} onNavClick={handleClick} navItems={navOverride}>
        {children}
      </SalesLayout>
    )
  }

  if (navItems) {
    return (
      <SidebarLayout profile={profile} navItems={navItems} activeTab={currentTab} onNavClick={handleClick} portalLabel={portalLabel}>
        {children}
      </SidebarLayout>
    )
  }

  // Student — top nav
  const cfg = ROLES[role] || ROLES.student
  return (
    <div style={{ minHeight:'100vh', background:'var(--bg-shell)' }}>
      <nav style={{ background:'var(--bg-page)', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.07)', height:'54px', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 1.5rem', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
          <div style={{ fontFamily:"'Arial Black',sans-serif", fontWeight:900 }}>
            <span style={{ color:'#1e90ff', fontSize:'18px' }}>UNI</span>
            <span style={{ color:'#e87c1e', fontSize:'18px' }}>EDD</span>
          </div>
          {pageTitle && <span style={{ fontSize:'15px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a55))' }}>{pageTitle}</span>}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
          <span style={{ fontSize:'10px', padding:'3px 10px', borderRadius:'20px', fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase', background:cfg.bg, color:cfg.color, border:`0.5px solid ${cfg.border}` }}>{cfg.label}</span>
          <ProfilePhoto profile={profile} size={32} />
          <ThemeToggle compact />
          <button onClick={() => supabase.auth.signOut()} style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))', background:'none', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'6px', padding:'5px 11px', cursor:'pointer', fontFamily:'inherit' }}>Sign out</button>
        </div>
      </nav>
      <main style={{ padding:'1.5rem', maxWidth:'1200px', margin:'0 auto' }}>
        {children}
      </main>
    </div>
  )
}

// Tracks which tabs have ever been visited, so callers can keep them
// mounted (via <Keep>) instead of destroying their state on every switch.
export function useTabs(defaultTab) {
  const [tab, setTab] = useState(defaultTab)
  const [visited, setVisited] = useState(() => new Set([defaultTab]))
  function go(id) {
    setTab(id)
    setVisited(prev => prev.has(id) ? prev : new Set(prev).add(id))
  }
  return { tab, go, has: id => visited.has(id) }
}

// Keeps children mounted (state intact) while toggling visibility with CSS,
// instead of the `cond && <X/>` pattern which unmounts/remounts on every switch.
export function Keep({ show, children }) {
  return <div style={{ display: show ? 'block' : 'none' }}>{children}</div>
}

export function PageHeader({ title, subtitle }) {
  return (
    <div id="uniedd-top" style={{ marginBottom:'1.5rem' }}>
      <h1 style={{ fontSize:'26px', fontWeight:800, color:'var(--fg-solid)', letterSpacing:'-0.03em', margin:0 }}>{title}</h1>
      {subtitle && <p style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a28))', marginTop:'5px' }}>{subtitle}</p>}
    </div>
  )
}

export function Grid4({ children, style }) {
  return <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'12px', marginBottom:'1.25rem', ...style }}>{children}</div>
}

export function MetricCard({ icon, label, value, sub, onClick, accentColor }) {
  return (
    <div onClick={onClick} style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'16px', cursor:onClick?'pointer':'default', transition:'border-color 0.15s' }}
      onMouseOver={e=>onClick&&(e.currentTarget.style.borderColor='rgba(var(--fg-rgb),0.15)')}
      onMouseOut={e=>onClick&&(e.currentTarget.style.borderColor='rgba(var(--fg-rgb),0.07)')}>
      <div style={{ fontSize:'20px', marginBottom:'10px' }}>{icon}</div>
      <div style={{ fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a28))', letterSpacing:'0.1em', textTransform:'uppercase' }}>{label}</div>
      <div style={{ fontSize:'26px', fontWeight:800, color: accentColor || 'var(--fg-solid)', letterSpacing:'-0.02em', marginTop:'4px' }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a22))', marginTop:'2px' }}>{sub}</div>}
    </div>
  )
}

export function Panel({ title, children, style }) {
  return (
    <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'1.1rem 1.2rem', ...style }}>
      {title && <div style={{ fontSize:'13px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a65))', marginBottom:'14px', paddingBottom:'10px', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.06)' }}>{title}</div>}
      {children}
    </div>
  )
}

export function TwoCol({ children }) {
  return <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>{children}</div>
}

export function Lbl({ children }) {
  return <label style={{ display:'block', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a32))', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'6px', marginTop:'14px' }}>{children}</label>
}

export function Inp(props) {
  return <input {...props} style={{ width:'100%', background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'11px 13px', fontSize:'14px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', display:'block', fontFamily:'inherit', boxSizing:'border-box', ...props.style }}
    onFocus={e => e.target.style.borderColor='#1e90ff'}
    onBlur={e  => e.target.style.borderColor='rgba(var(--fg-rgb),0.1)'} />
}

export function Btn({ busy, children, style, color, ...props }) {
  const bg = color || '#1e90ff'
  return (
    <button {...props} disabled={busy || props.disabled} style={{ width:'100%', padding:'12px', background:bg, color:'#fff', fontSize:'14px', fontWeight:700, border:'none', borderRadius:'10px', cursor:'pointer', marginTop:'12px', fontFamily:'inherit', opacity:(busy||props.disabled)?0.6:1, ...style }}>
      {busy ? 'Please wait...' : children}
    </button>
  )
}

export function Err({ msg }) {
  if (!msg) return null
  return <div style={{ marginTop:'8px', padding:'8px 12px', background:'rgba(220,60,60,0.1)', border:'0.5px solid rgba(220,60,60,0.3)', borderRadius:'8px', fontSize:'13px', color:'#f09595' }}>{msg}</div>
}

export function Ok({ msg }) {
  if (!msg) return null
  return <div style={{ marginTop:'8px', padding:'8px 12px', background:'rgba(16,185,129,0.1)', border:'0.5px solid rgba(16,185,129,0.3)', borderRadius:'8px', fontSize:'13px', color:'#34d399' }}>{msg}</div>
}

export function Row({ name, sub, right }) {
  const ini = (name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 0', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.05)' }}>
      <div style={{ width:'30px', height:'30px', borderRadius:'50%', flexShrink:0, background:'rgba(139,92,246,0.2)', color:'#a78bfa', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:800 }}>{ini}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{name}</div>
        {sub && <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'1px' }}>{sub}</div>}
      </div>
      {right}
    </div>
  )
}

export function Empty({ msg, children }) {
  return <div style={{ textAlign:'center', padding:'2.5rem 1rem', color:'rgba(var(--fg-rgb),0.18)', fontSize:'13px' }}>{msg || children || 'Nothing here yet.'}</div>
}

export function Pill({ status }) {
  const M = {
    new:{bg:'rgba(16,185,129,0.15)',c:'#10b981',t:'New'}, contacted:{bg:'rgba(30,144,255,0.15)',c:'#5aabff',t:'Contacted'}, 'New':{bg:'rgba(16,185,129,0.15)',c:'#10b981',t:'New'}, 'Contacted':{bg:'rgba(30,144,255,0.15)',c:'#5aabff',t:'Contacted'}, demo_scheduled:{bg:'rgba(232,124,30,0.15)',c:'#f4a335',t:'Demo Set'}, 'Demo Scheduled':{bg:'rgba(232,124,30,0.15)',c:'#f4a335',t:'Demo Set'}, enrolled:{bg:'rgba(139,92,246,0.15)',c:'#a78bfa',t:'Enrolled'}, 'Enrolled':{bg:'rgba(139,92,246,0.15)',c:'#a78bfa',t:'Enrolled'}, lost:{bg:'rgba(100,100,100,0.15)',c:'#888',t:'Lost'}, 'Lost':{bg:'rgba(100,100,100,0.15)',c:'#888',t:'Lost'}, scheduled:{bg:'rgba(30,144,255,0.15)',c:'#5aabff',t:'Scheduled'}, completed:{bg:'rgba(16,185,129,0.15)',c:'#34d399',t:'Done'}, 'Demo Payment Received':{bg:'rgba(16,185,129,0.15)',c:'#34d399',t:'💳 Paid — Schedule'}
  }
  const s = M[status] || { bg:'rgba(100,100,100,0.15)', c:'#aaa', t:status }
  return <span style={{ fontSize:'9px', fontWeight:700, padding:'3px 8px', borderRadius:'10px', background:s.bg, color:s.c, whiteSpace:'nowrap', flexShrink:0 }}>{s.t}</span>
}

export function ZoomBtn({ link, onClick }) {
  if (!link) return <span style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a20))', flexShrink:0 }}>Link pending</span>
  return <a href={link} target="_blank" rel="noreferrer" onClick={onClick} style={{ fontSize:'10px', fontWeight:700, padding:'4px 12px', borderRadius:'8px', background:'rgba(30,144,255,0.15)', color:'#5aabff', border:'0.5px solid rgba(30,144,255,0.25)', flexShrink:0, whiteSpace:'nowrap' }}>Join Class</a>
}
